import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Plus, X, Crown, Lock, Globe, Eye, BadgeCheck, ShieldCheck, Clock, XCircle, Sparkles, UploadCloud, Loader2, Rocket, Volume2, Wand2, Ban, ArrowRight, LogOut, Bell, LifeBuoy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { uploadToStorage, mediaUrl } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import { PROFILE_THEMES, THEME_NAMES, getTheme } from "@/lib/themes";

const GENDERS = ["Woman", "Man", "Non-binary", "Trans woman", "Trans man", "Genderfluid", "Other"];
const ORIENTATIONS = ["Straight", "Gay", "Lesbian", "Bisexual", "Pansexual", "Queer", "Curious"];
const LOOKING_FOR = ["Casual fun", "Dating", "Friends", "Play partners", "Long-term", "Just chatting"];
const PROMPT_QUESTIONS = [
  "My idea of a perfect night is…",
  "I get flirty when…",
  "Two truths and a lie…",
  "My biggest turn-on is…",
  "You should message me if…",
  "My secret talent is…",
  "The way to my heart is…",
  "Don't tell my mother, but…",
];

export default function ProfileEdit() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [f, setF] = useState({
    name: user?.name || "", age: user?.age || "", gender: user?.gender || "",
    orientation: user?.orientation || "", location: user?.location || "",
    bio: user?.bio || "", theme: user?.theme || "Midnight Gold",
    photos: user?.photos || [], interests: user?.interests || [],
    is_creator: user?.is_creator || false, is_private: user?.is_private || false,
    subscription_price: user?.subscription_price || "",
    looking_for: user?.looking_for || [], prompts: user?.prompts || [],
    skin_bg: user?.skin_bg || "", skin_accent: user?.skin_accent || "",
    cover_photo: user?.cover_photo || "",
    fantasy_match_notify: user?.fantasy_match_notify || false,
  });
  const [uploadingSkin, setUploadingSkin] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [photoInput, setPhotoInput] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [interestInput, setInterestInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [cfg, setCfg] = useState({});
  useEffect(() => { api.get("/site-config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const [tiers, setTiers] = useState(
    (user?.tiers && user.tiers.length
      ? user.tiers
      : [{ name: "Subscription", price: user?.subscription_price || "", description: "" }]
    ).map((t) => ({ name: t.name, price: t.price, description: t.description || "" }))
  );
  const [savingTiers, setSavingTiers] = useState(false);
  const theme = getTheme(f.theme);

  const surpriseMe = () => {
    const accents = ["#F5C542", "#FB7185", "#60A5FA", "#34D399", "#C084FC", "#E879F9", "#D4AF37", "#FF6B6B"];
    let name = f.theme;
    while (name === f.theme) name = THEME_NAMES[Math.floor(Math.random() * THEME_NAMES.length)];
    const acc = accents[Math.floor(Math.random() * accents.length)];
    setF((prev) => ({ ...prev, theme: name, skin_accent: acc }));
    toast.success(`✨ Surprise! "${name}" — hit Save profile to keep it`);
  };

  const addTier = () => { if (tiers.length < 3) setTiers([...tiers, { name: "", price: "", description: "" }]); };
  const removeTier = (i) => setTiers(tiers.filter((_, j) => j !== i));
  const updateTier = (i, k, v) => setTiers(tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  const saveTiers = async () => {
    const clean = tiers.filter((t) => t.name.trim() && t.price !== "" && t.price !== null);
    if (!clean.length) { toast.error("Add at least one tier with a name and price"); return; }
    setSavingTiers(true);
    try {
      await api.put("/creator/tiers", { tiers: clean.map((t) => ({ name: t.name.trim(), price: Number(t.price), description: t.description })) });
      await refreshUser();
      toast.success("Subscription tiers saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingTiers(false); }
  };

  // Handle Stripe creator-membership return
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Creator membership active for 30 days 👑");
          setParams({}); await refreshUser(); return;
        }
        if (data.status === "expired") { toast.error("Payment expired"); setParams({}); return; }
      } catch (e) {}
      if (attempts++ < 5) setTimeout(poll, 2000);
      else { setParams({}); }
    };
    toast.message("Confirming membership payment…");
    poll();
  }, [params, setParams, refreshUser]);

  const payMembership = async () => {
    setPayLoading(true);
    try {
      const { data } = await api.post("/creator/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPayLoading(false);
    }
  };

  const activateFree = async () => {
    setPayLoading(true);
    try {
      await api.post("/creator/activate-free");
      await refreshUser();
      toast.success("Founding Creator membership activated — free until launch 👑");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPayLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", {
        ...f, age: f.age ? Number(f.age) : null,
        subscription_price: f.subscription_price ? Number(f.subscription_price) : 0,
        prompts: (f.prompts || []).filter((p) => p.q && p.a && p.a.trim()),
      });
      await refreshUser();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const toggleLooking = (v) =>
    setF({ ...f, looking_for: f.looking_for.includes(v) ? f.looking_for.filter((x) => x !== v) : [...f.looking_for, v] });
  const addPrompt = () => {
    const used = (f.prompts || []).map((p) => p.q);
    const next = PROMPT_QUESTIONS.find((q) => !used.includes(q));
    if (f.prompts.length < 3 && next) setF({ ...f, prompts: [...f.prompts, { q: next, a: "" }] });
  };
  const updatePrompt = (i, key, val) => setF({ ...f, prompts: f.prompts.map((p, j) => (j === i ? { ...p, [key]: val } : p)) });
  const removePrompt = (i) => setF({ ...f, prompts: f.prompts.filter((_, j) => j !== i) });

  const uploadSkin = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image");
    if (file.size > 15 * 1024 * 1024) return toast.error("Image is too large (max 15MB)");
    setUploadingSkin(true);
    try {
      const path = await uploadToStorage(file, file.name, file.type, "photo", null);
      setF((prev) => ({ ...prev, skin_bg: mediaUrl(path) }));
      toast.success("Skin background set — save to publish ✨");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploadingSkin(false); }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image");
    if (file.size > 15 * 1024 * 1024) return toast.error("Image is too large (max 15MB)");
    setUploadingCover(true);
    try {
      const path = await uploadToStorage(file, file.name, file.type, "photo", null);
      setF((prev) => ({ ...prev, cover_photo: mediaUrl(path) }));
      toast.success("Cover banner set — save to publish ✨");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploadingCover(false); }
  };

  const completion = (() => {
    const checks = [
      f.photos.length > 0, (f.bio || "").trim().length > 0, f.interests.length > 0,
      !!f.age, !!f.gender, !!f.orientation, (f.location || "").trim().length > 0,
      f.looking_for.length > 0, (f.prompts || []).some((p) => p.a && p.a.trim()),
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  })();

  const addPhoto = () => { if (photoInput.trim()) { setF({ ...f, photos: [...f.photos, photoInput.trim()] }); setPhotoInput(""); } };
  const uploadProfilePhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 25 * 1024 * 1024) return toast.error("Image too large (max 25MB)");
    setUploadingPhoto(true);
    try {
      const path = await uploadToStorage(file, file.name, file.type, "photo", null);
      setF((cur) => ({ ...cur, photos: [...cur.photos, mediaUrl(path)] }));
      toast.success("Photo uploaded — remember to Save profile");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed — please try again");
    } finally { setUploadingPhoto(false); }
  };
  const addInterest = () => { if (interestInput.trim()) { setF({ ...f, interests: [...f.interests, interestInput.trim()] }); setInterestInput(""); } };
  const [fantasies, setFantasies] = useState([]);
  useEffect(() => { api.get("/fantasies").then((r) => setFantasies(r.data)).catch(() => {}); }, []);
  const toggleFantasy = (label) => {
    setF((prev) => prev.interests.includes(label)
      ? { ...prev, interests: prev.interests.filter((x) => x !== label) }
      : { ...prev, interests: [...prev.interests, label] });
  };

  const [empower, setEmpower] = useState(null);
  useEffect(() => {
    if (user?.is_creator) api.get("/creator/empower").then((r) => setEmpower(r.data)).catch(() => {});
  }, [user?.is_creator]);
  const saveEmpower = async (patch) => {
    const next = { enabled: empower?.enabled ?? true, frequency: empower?.frequency ?? "weekly", channel: empower?.channel ?? "both", ...patch };
    setEmpower((e) => ({ ...(e || {}), ...next }));
    try { const { data } = await api.put("/creator/empower", next); setEmpower((e) => ({ ...(e || {}), ...data })); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const testEmpower = async () => {
    try { const { data } = await api.post("/creator/empower/test"); toast.success("A royal reminder is on its way to your inbox 👑"); setEmpower((e) => ({ ...(e || {}), quote: data.quote })); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [voiceLoading, setVoiceLoading] = useState(false);
  const playVoice = async () => {
    setVoiceLoading(true);
    try {
      const { data } = await api.get("/creator/empower/voice");
      await new Audio(`data:audio/mp3;base64,${data.audio}`).play();
    } catch (err) { toast.error("Couldn't play the audio right now"); }
    finally { setVoiceLoading(false); }
  };

  return (
    <div className="p-6 md:p-12 max-w-5xl mx-auto">
      <div className="overline mb-1">Your presence</div>
      <div className="flex items-center justify-between mb-8 gap-3 flex-wrap">
        <h2 className="font-heading text-3xl md:text-4xl">Edit Profile</h2>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button data-testid="profile-help-btn" onClick={() => navigate("/help")}
            className="btn-outline-gold px-5 py-2 rounded-full text-sm flex items-center gap-2">
            <LifeBuoy size={16} /> Help
          </button>
          <button data-testid="view-my-profile" onClick={() => navigate(`/creator/${user.id}`)}
            className="btn-outline-gold px-5 py-2 rounded-full text-sm flex items-center gap-2">
            <Eye size={16} /> View my profile
          </button>
          <button data-testid="profile-logout-btn" onClick={() => { logout(); navigate("/"); }}
            className="px-5 py-2 rounded-full text-sm flex items-center gap-2 border border-[#E63946]/40 text-[#E63946] hover:bg-[#E63946]/10 transition-colors">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </div>

      {/* Admin shortcuts (mobile-friendly entry to admin tools) */}
      {user?.role === "admin" && (
        <div data-testid="admin-shortcuts" className="rounded-2xl p-5 mb-6 border border-[#D4AF37]/30 bg-gradient-to-r from-[#1A1508] to-[#0A0A0A]">
          <div className="flex items-center gap-2 mb-3"><Crown size={18} className="text-[#D4AF37]" /><span className="font-heading text-xl text-white">Admin tools</span></div>
          <div className="flex flex-wrap gap-3">
            <button data-testid="go-command" onClick={() => navigate("/admin/command")}
              className="btn-gold px-5 py-2.5 rounded-full text-sm flex items-center gap-2"><Crown size={16} /> Command Center</button>
            <button data-testid="go-verifications" onClick={() => navigate("/admin/verifications")}
              className="btn-outline-gold px-5 py-2.5 rounded-full text-sm flex items-center gap-2"><ShieldCheck size={16} /> Verify Users / Vault</button>
          </div>
        </div>
      )}

      {/* Royal affirmation (in-app) */}
      {user?.is_creator && empower?.quote && (
        <motion.div data-testid="royal-affirmation" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-5 mb-6 border border-[#D4AF37]/30 bg-gradient-to-r from-[#1A1508] to-[#0A0A0A]">
          <div className="flex items-start gap-3">
            <Crown size={20} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="overline mb-1">Your royal reminder</div>
              <div className="font-heading text-lg md:text-xl text-white italic">&ldquo;{empower.quote}&rdquo;</div>
              <button type="button" data-testid="empower-play" onClick={playVoice} disabled={voiceLoading}
                className="mt-2 text-xs text-[#D4AF37] hover:text-[#F5C542] inline-flex items-center gap-1.5 disabled:opacity-60">
                {voiceLoading ? <Loader2 size={13} className="animate-spin" /> : <Volume2 size={13} />} {voiceLoading ? "Preparing…" : "Listen"}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Profile completeness */}
      <div data-testid="completeness-meter" className="pe-card rounded-2xl p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-white font-medium">Profile completeness</div>
          <div className="text-sm gold-text font-heading">{completion}%</div>
        </div>
        <div className="h-2 rounded-full bg-[#1A1A1A] overflow-hidden">
          <div className="h-full gold-gradient transition-all" style={{ width: `${completion}%` }} />
        </div>
        {completion < 100 && <div className="text-[11px] text-[#A0A0A0] mt-2">Complete your profile to get more matches — add photos, a bio, what you're looking for & a fun prompt.</div>}
      </div>

      {/* Avatar Atelier CTA */}
      <div data-testid="avatar-cta" className="pe-card rounded-2xl p-5 mb-8 flex items-center gap-4">
        <Sparkles size={28} className="text-[#D4AF37]" />
        <div className="flex-1">
          <div className="text-white font-medium">Craft your alter ego</div>
          <div className="text-xs text-[#A0A0A0]">Design an elegant AI avatar — masks, outfits & more. Free for everyone.</div>
        </div>
        <button data-testid="go-avatar" onClick={() => navigate("/avatar")}
          className="btn-gold px-5 py-2 rounded-full text-sm whitespace-nowrap">Open Atelier</button>
      </div>

      {/* Verification status */}
      {(() => {
        const vs = user?.verification_status || (user?.verified ? "approved" : "none");
        const map = {
          none: { icon: ShieldCheck, color: "#A0A0A0", label: "Not verified", cta: "Get verified" },
          pending: { icon: Clock, color: "#D4AF37", label: "Verification under review", cta: "View status" },
          approved: { icon: BadgeCheck, color: "#22c55e", label: "Verified member", cta: null },
          rejected: { icon: XCircle, color: "#E63946", label: "Verification rejected", cta: "Resubmit" },
        };
        const m = map[vs] || map.none;
        const VIcon = m.icon;
        return (
          <div data-testid="profile-verify-card" className="pe-card rounded-2xl p-5 mb-8 flex items-center gap-4">
            <VIcon size={28} style={{ color: m.color }} />
            <div className="flex-1">
              <div className="text-white font-medium">{m.label}</div>
              <div className="text-xs text-[#A0A0A0]">Verified members get a gold badge, more trust & creator payouts.</div>
            </div>
            {m.cta && (
              <button data-testid="go-verify" onClick={() => navigate("/verify")}
                className="btn-outline-gold px-5 py-2 rounded-full text-sm whitespace-nowrap">{m.cta}</button>
            )}
          </div>
        );
      })()}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Form */}
        <div className="space-y-4">
          <input data-testid="pf-name" placeholder="Display name" value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })} className="pe-input w-full rounded-lg px-4 py-3" />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="pf-age" type="number" placeholder="Age" value={f.age}
              onChange={(e) => setF({ ...f, age: e.target.value })} className="pe-input rounded-lg px-4 py-3" />
            <input data-testid="pf-location" placeholder="Location" value={f.location}
              onChange={(e) => setF({ ...f, location: e.target.value })} className="pe-input rounded-lg px-4 py-3" />
          </div>
          <select data-testid="pf-gender" value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })}
            className="pe-input w-full rounded-lg px-4 py-3">
            <option value="">Gender identity</option>
            {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <select data-testid="pf-orientation" value={f.orientation} onChange={(e) => setF({ ...f, orientation: e.target.value })}
            className="pe-input w-full rounded-lg px-4 py-3">
            <option value="">Orientation</option>
            {ORIENTATIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <textarea data-testid="pf-bio" placeholder="Tell them what you're into…" value={f.bio}
            onChange={(e) => setF({ ...f, bio: e.target.value })} className="pe-input w-full rounded-lg px-4 py-3 h-28" />

          {/* Photos */}
          <div>
            <div className="text-sm text-[#A0A0A0] mb-2">Photos</div>
            <label data-testid="pf-upload-photo"
              className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#2A2A2A] hover:border-[#D4AF37]/60 cursor-pointer py-3 mb-2 text-sm text-white transition-colors">
              {uploadingPhoto ? <Loader2 size={16} className="animate-spin text-[#D4AF37]" /> : <UploadCloud size={16} className="text-[#D4AF37]" />}
              {uploadingPhoto ? "Uploading…" : "Upload from your device"}
              <input type="file" accept="image/*" className="hidden" disabled={uploadingPhoto}
                onChange={(e) => uploadProfilePhoto(e.target.files?.[0])} />
            </label>
            <div className="flex gap-2 mb-2">
              <input data-testid="pf-photo-input" placeholder="…or paste an image URL" value={photoInput}
                onChange={(e) => setPhotoInput(e.target.value)} className="pe-input flex-1 rounded-lg px-4 py-2" />
              <button data-testid="pf-add-photo" onClick={addPhoto} className="btn-outline-gold px-4 rounded-lg"><Plus size={18} /></button>
            </div>
            <div className="flex flex-wrap gap-2">
              {f.photos.map((p, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#2A2A2A]">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setF({ ...f, photos: f.photos.filter((_, j) => j !== i) })}
                    className="absolute top-0 right-0 bg-black/70 text-white p-1"><X size={12} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Interests */}
          <div>
            <div className="text-sm text-[#A0A0A0] mb-2">Interests / Kinks</div>
            <div className="flex gap-2 mb-2">
              <input data-testid="pf-interest-input" placeholder="Add a tag" value={interestInput}
                onChange={(e) => setInterestInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addInterest(); } }}
                className="pe-input flex-1 rounded-lg px-4 py-2" />
              <button data-testid="pf-add-interest" onClick={addInterest} className="btn-outline-gold px-4 rounded-lg"><Plus size={18} /></button>
            </div>
            <div className="flex flex-wrap gap-2">
              {f.interests.map((t, i) => (
                <span key={i} className="text-xs px-3 py-1 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] flex items-center gap-1">
                  {t} <button onClick={() => setF({ ...f, interests: f.interests.filter((_, j) => j !== i) })}><X size={12} /></button>
                </span>
              ))}
            </div>
          </div>

          {/* Fantasy menu */}
          <button type="button" data-testid="profile-quiz-link" onClick={() => navigate("/quiz")}
            className="btn-outline-gold w-full rounded-full py-2 text-sm">Take or retake the desire quiz</button>
          {fantasies.length > 0 && (
            <div data-testid="fantasy-menu">
              <div className="text-sm text-[#A0A0A0] mb-1">Explore your fantasies</div>
              <div className="text-xs text-[#A0A0A0] mb-3">Tap what excites you — it adds to your profile and helps us match you.</div>
              <div className="space-y-4">
                {fantasies.map((grp) => (
                  <div key={grp.group}>
                    <div className="text-sm text-white font-medium mb-2">{grp.emoji} {grp.group}</div>
                    <div className="flex flex-wrap gap-2">
                      {grp.items.map((item) => {
                        const on = f.interests.includes(item);
                        return (
                          <button type="button" key={item} data-testid={`fantasy-${item}`} onClick={() => toggleFantasy(item)}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "gold-gradient text-black font-medium border-transparent" : "border-[#2A2A2A] text-[#A0A0A0] hover:text-white hover:border-[#D4AF37]/50"}`}>
                            {on ? "✓ " : ""}{item}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div data-testid="fantasy-notify-card" className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-[#2A2A2A] p-4">
                <div className="flex items-start gap-3">
                  <Bell size={18} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm text-white font-medium">Notify me about shared fantasies</div>
                    <div className="text-xs text-[#A0A0A0]">When you match with someone who shares your fantasies, we'll highlight it in your match email.</div>
                  </div>
                </div>
                <button type="button" data-testid="toggle-fantasy-notify" onClick={() => setF({ ...f, fantasy_match_notify: !f.fantasy_match_notify })}
                  className={`w-12 h-7 rounded-full transition-colors relative flex-shrink-0 ${f.fantasy_match_notify ? "gold-gradient" : "bg-[#2A2A2A]"}`}>
                  <span className={`absolute top-1 w-5 h-5 rounded-full bg-black transition-all ${f.fantasy_match_notify ? "left-6" : "left-1"}`} />
                </button>
              </div>
            </div>
          )}
          <div>
            <div className="text-sm text-[#A0A0A0] mb-2">I'm looking for</div>
            <div className="flex flex-wrap gap-2">
              {LOOKING_FOR.map((v) => (
                <button key={v} type="button" data-testid={`looking-${v}`} onClick={() => toggleLooking(v)}
                  className={`text-xs px-3 py-1.5 rounded-full transition-colors ${f.looking_for.includes(v) ? "gold-gradient text-black font-medium" : "border border-[#2A2A2A] text-[#A0A0A0] hover:text-white"}`}>{v}</button>
              ))}
            </div>
          </div>

          {/* Profile prompts */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-[#A0A0A0]">Profile prompts <span className="text-[#555]">(up to 3)</span></div>
              {f.prompts.length < 3 && <button type="button" data-testid="add-prompt" onClick={addPrompt} className="text-xs text-[#D4AF37] flex items-center gap-1"><Plus size={12} /> Add prompt</button>}
            </div>
            <div className="space-y-3">
              {f.prompts.map((p, i) => (
                <div key={i} data-testid={`prompt-${i}`} className="rounded-lg border border-[#2A2A2A] p-3 space-y-2">
                  <div className="flex gap-2 items-center">
                    <select data-testid={`prompt-q-${i}`} value={p.q} onChange={(e) => updatePrompt(i, "q", e.target.value)}
                      className="pe-input flex-1 rounded-lg px-3 py-2 text-sm">
                      {PROMPT_QUESTIONS.filter((q) => q === p.q || !f.prompts.some((x) => x.q === q)).map((q) => <option key={q} value={q}>{q}</option>)}
                    </select>
                    <button type="button" data-testid={`remove-prompt-${i}`} onClick={() => removePrompt(i)} className="text-[#E63946] px-1"><X size={16} /></button>
                  </div>
                  <input data-testid={`prompt-a-${i}`} placeholder="Your answer…" value={p.a} maxLength={140}
                    onChange={(e) => updatePrompt(i, "a", e.target.value)} className="pe-input w-full rounded-lg px-3 py-2 text-sm" />
                </div>
              ))}
              {f.prompts.length === 0 && <div className="text-[11px] text-[#6b6b6b]">Add a fun prompt to show your personality — great conversation starters.</div>}
            </div>
          </div>


          <button data-testid="save-profile" onClick={save} disabled={saving}
            className="btn-gold w-full rounded-full py-3 disabled:opacity-60">{saving ? "Saving…" : "Save profile"}</button>

          {/* Privacy */}
          <div className="pe-card rounded-2xl p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lock size={20} className="text-[#D4AF37]" />
              <div>
                <div className="text-sm text-white font-medium">Private profile</div>
                <div className="text-xs text-[#A0A0A0]">Hide photos & bio until you accept a friend request.</div>
              </div>
            </div>
            <button data-testid="toggle-private" onClick={() => setF({ ...f, is_private: !f.is_private })}
              className={`w-12 h-7 rounded-full transition-colors relative ${f.is_private ? "gold-gradient" : "bg-[#2A2A2A]"}`}>
              <span className={`absolute top-1 w-5 h-5 rounded-full bg-black transition-all ${f.is_private ? "left-6" : "left-1"}`} />
            </button>
          </div>

          {/* Safety: blocked members */}
          <button data-testid="blocked-link" onClick={() => navigate("/blocked")}
            className="pe-card rounded-2xl p-5 flex items-center justify-between w-full text-left hover:border-[#D4AF37]/40 transition-colors">
            <div className="flex items-center gap-3">
              <Ban size={20} className="text-[#E63946]" />
              <div>
                <div className="text-sm text-white font-medium">Blocked members</div>
                <div className="text-xs text-[#A0A0A0]">See and unblock people you've blocked.</div>
              </div>
            </div>
            <ArrowRight size={18} className="text-[#A0A0A0]" />
          </button>

          {/* Creator Studio */}
          <div className="pe-card rounded-2xl p-5 space-y-4">
            <div className="flex items-center gap-2"><Crown size={20} className="text-[#D4AF37]" /><span className="font-heading text-2xl">Creator Studio</span></div>
            <div className="flex items-center justify-between">
              <div className="text-sm text-[#A0A0A0]">Enable Creator mode to post paid content & earn.</div>
              <button data-testid="toggle-creator" onClick={() => setF({ ...f, is_creator: !f.is_creator })}
                className={`w-12 h-7 rounded-full transition-colors relative ${f.is_creator ? "gold-gradient" : "bg-[#2A2A2A]"}`}>
                <span className={`absolute top-1 w-5 h-5 rounded-full bg-black transition-all ${f.is_creator ? "left-6" : "left-1"}`} />
              </button>
            </div>
            {f.is_creator && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-[#A0A0A0]">Subscription tiers (up to 3)</div>
                  {tiers.length < 3 && (
                    <button type="button" data-testid="add-tier" onClick={addTier} className="text-xs text-[#D4AF37] flex items-center gap-1"><Plus size={12} /> Add tier</button>
                  )}
                </div>
                {tiers.map((t, i) => (
                  <div key={i} data-testid={`tier-${i}`} className="rounded-lg border border-[#2A2A2A] p-3 space-y-2">
                    <div className="flex gap-2">
                      <input data-testid={`tier-name-${i}`} placeholder="Tier name (e.g. VIP)" value={t.name}
                        onChange={(e) => updateTier(i, "name", e.target.value)} className="pe-input flex-1 rounded-lg px-3 py-2 text-sm" />
                      <div className="flex items-center gap-1">
                        <span className="text-[#D4AF37]">$</span>
                        <input data-testid={`tier-price-${i}`} type="number" step="0.01" placeholder="9.99" value={t.price}
                          onChange={(e) => updateTier(i, "price", e.target.value)} className="pe-input w-24 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      {tiers.length > 1 && (
                        <button type="button" data-testid={`remove-tier-${i}`} onClick={() => removeTier(i)} className="text-[#E63946] px-1"><X size={16} /></button>
                      )}
                    </div>
                    <input data-testid={`tier-desc-${i}`} placeholder="What subscribers at this tier get" value={t.description}
                      onChange={(e) => updateTier(i, "description", e.target.value)} className="pe-input w-full rounded-lg px-3 py-2 text-sm" />
                  </div>
                ))}
                <div className="text-[11px] text-[#6b6b6b]">Tiers are auto-ordered by price. Assign posts to a tier when publishing.</div>
                <button type="button" data-testid="save-tiers" onClick={saveTiers} disabled={savingTiers}
                  className="btn-outline-gold w-full rounded-full py-2 text-sm disabled:opacity-60">{savingTiers ? "Saving…" : "Save tiers"}</button>
              </div>
            )}

            {f.is_creator && (
              <button type="button" data-testid="open-creator-onboarding" onClick={() => navigate("/creator-onboarding")}
                className="w-full rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.05] px-4 py-3 flex items-center gap-3 text-left hover:bg-[#D4AF37]/[0.1] transition-colors">
                <Rocket size={18} className="text-[#D4AF37] flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-sm text-white font-medium">Creator setup checklist</div>
                  <div className="text-xs text-[#A0A0A0]">Track your steps to go live and start earning</div>
                </div>
                <span className="text-[#D4AF37] text-lg">→</span>
              </button>
            )}

            {/* Membership status */}
            <div className="border-t border-[#2A2A2A] pt-4">
              {user?.creator_active_now ? (
                <div>
                  <div className="text-sm text-[#D4AF37] flex items-center gap-2"><Check size={16} /> Membership active</div>
                  <div className="text-xs text-[#A0A0A0] mt-1">Renews/expires: {user?.creator_expires_at ? new Date(user.creator_expires_at).toLocaleDateString() : "—"}</div>
                  <button data-testid="view-my-creator" onClick={() => navigate(`/creator/${user.id}`)}
                    className="btn-outline-gold w-full rounded-full py-2 text-sm mt-3">Manage my content</button>
                  <button data-testid="view-subscribers" onClick={() => navigate("/subscribers")}
                    className="btn-outline-gold w-full rounded-full py-2 text-sm mt-2">View subscribers & earnings</button>
                  <button data-testid="view-analytics" onClick={() => navigate("/analytics")}
                    className="btn-outline-gold w-full rounded-full py-2 text-sm mt-2">View analytics 📈</button>
                  <button data-testid="view-payouts" onClick={() => navigate("/payouts")}
                    className="btn-outline-gold w-full rounded-full py-2 text-sm mt-2">Payouts & withdrawals 💸</button>
                </div>
              ) : (
                <div>
                  {cfg.founding_free_mode && user?.founding_creator ? (
                    <div data-testid="founding-free-panel">
                      <div className="text-sm text-[#D4AF37] font-medium flex items-center gap-2"><Crown size={16} /> Founding Creator perk</div>
                      <div className="text-xs text-[#A0A0A0] mt-1 mb-3">
                        Your $19.99/mo membership is <b className="text-white">free until launch</b>
                        {cfg.founding_launch_date ? <> ({new Date(cfg.founding_launch_date).toLocaleDateString()})</> : null}. Activate now and start earning — you keep 100% of your subscriptions & tips.
                      </div>
                      <button data-testid="activate-free-membership" onClick={activateFree} disabled={payLoading}
                        className="btn-gold w-full rounded-full py-3 disabled:opacity-60">
                        {payLoading ? "Activating…" : "Activate FREE — Founding Creator 👑"}
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="text-sm text-white font-medium">Creator membership required</div>
                      <div className="text-xs text-[#A0A0A0] mt-1 mb-3">Pay the monthly platform fee to activate your creator profile and start earning. Without it, your profile won't appear and you can't post.</div>
                      <button data-testid="pay-membership" onClick={payMembership} disabled={payLoading}
                        className="btn-gold w-full rounded-full py-3 disabled:opacity-60">
                        {payLoading ? "Redirecting…" : "Activate — $19.99/mo"}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Theme picker + preview */}
        <div>
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-sm text-[#A0A0A0]">Choose your profile theme</div>
            <button type="button" data-testid="surprise-me" onClick={surpriseMe}
              className="btn-outline-gold rounded-full px-4 py-1.5 text-xs inline-flex items-center gap-1.5 hover:scale-105 transition-transform">
              <Wand2 size={13} /> Surprise me
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-6">
            {THEME_NAMES.map((name) => {
              const t = PROFILE_THEMES[name];
              const selected = f.theme === name;
              return (
                <button key={name} data-testid={`theme-${name}`} onClick={() => setF({ ...f, theme: name })}
                  className="rounded-xl p-3 text-left border transition-all relative"
                  style={{ background: t.surface, borderColor: selected ? t.accent : "#2A2A2A", borderWidth: selected ? 2 : 1 }}>
                  <div className="flex gap-1 mb-2">
                    {[t.background, t.accent, t.border].map((c, i) => (
                      <span key={i} className="w-5 h-5 rounded-full border border-white/10" style={{ background: c }} />
                    ))}
                  </div>
                  <div className="font-heading text-lg" style={{ color: t.text }}>{name}</div>
                  {selected && <Check size={16} className="absolute top-3 right-3" style={{ color: t.accent }} />}
                </button>
              );
            })}
          </div>

          <div className="mb-6 rounded-xl border border-[#D4AF37]/30 p-4 space-y-3" data-testid="cover-banner-card">
              <div className="text-sm text-white font-medium flex items-center gap-2"><Sparkles size={16} className="text-[#D4AF37]" /> Cover banner</div>
              <div className="text-xs text-[#A0A0A0]">A bold wide image across the top of your profile — your big first impression. Falls back to your first photo if empty.</div>
              {f.cover_photo && (
                <div className="relative rounded-lg overflow-hidden">
                  <img src={f.cover_photo} alt="cover" className="w-full h-28 object-cover" />
                  <button type="button" data-testid="clear-cover" onClick={() => setF({ ...f, cover_photo: "" })}
                    className="absolute top-2 right-2 bg-black/70 border border-white/20 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1"><X size={12} /> Remove</button>
                </div>
              )}
              <label data-testid="cover-upload" className="btn-outline-gold rounded-full px-5 py-2 text-sm inline-flex items-center gap-2 cursor-pointer">
                {uploadingCover ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><UploadCloud size={14} /> {f.cover_photo ? "Replace banner" : "Upload cover banner"}</>}
                <input type="file" accept="image/*" className="hidden" data-testid="cover-file-input" onChange={(e) => uploadCover(e.target.files?.[0])} />
              </label>
            </div>

          <div className="mb-6 rounded-xl border border-[#D4AF37]/30 p-4 space-y-3" data-testid="custom-skin-card">
              <div className="text-sm text-white font-medium flex items-center gap-2"><Sparkles size={16} className="text-[#D4AF37]" /> Your custom skin</div>
              <div className="text-xs text-[#A0A0A0]">Upload your own picture as the backdrop of your entire profile. It pairs with your chosen theme's colours.</div>
              {f.skin_bg && (
                <div className="relative rounded-lg overflow-hidden">
                  <img src={f.skin_bg} alt="skin" className="w-full h-40 object-cover" />
                  <button type="button" data-testid="clear-skin" onClick={() => setF({ ...f, skin_bg: "" })}
                    className="absolute top-2 right-2 bg-black/70 border border-white/20 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1"><X size={12} /> Remove</button>
                </div>
              )}
              <label data-testid="skin-upload" className="btn-outline-gold rounded-full px-5 py-2 text-sm inline-flex items-center gap-2 cursor-pointer">
                {uploadingSkin ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><UploadCloud size={14} /> {f.skin_bg ? "Replace background" : "Upload your background"}</>}
                <input type="file" accept="image/*" className="hidden" data-testid="skin-file-input" onChange={(e) => uploadSkin(e.target.files?.[0])} />
              </label>
              <div>
                <div className="text-xs text-[#A0A0A0] mb-1.5">Glow & accent colour</div>
                <div className="flex flex-wrap items-center gap-2">
                  {["#F5C542", "#FB7185", "#60A5FA", "#34D399", "#C084FC", "#E879F9"].map((c) => (
                    <button type="button" key={c} data-testid={`skin-accent-${c}`} onClick={() => setF({ ...f, skin_accent: c })}
                      className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${f.skin_accent === c ? "border-white" : "border-transparent"}`} style={{ background: c }} />
                  ))}
                  <label className="relative w-7 h-7 rounded-full border-2 border-dashed border-white/40 flex items-center justify-center cursor-pointer overflow-hidden" title="Custom colour">
                    <input type="color" data-testid="skin-accent-custom" value={f.skin_accent || "#D4AF37"} onChange={(e) => setF({ ...f, skin_accent: e.target.value })} className="absolute inset-0 opacity-0 cursor-pointer" />
                    <Plus size={12} className="text-white/70" />
                  </label>
                  {f.skin_accent && <button type="button" data-testid="skin-accent-clear" onClick={() => setF({ ...f, skin_accent: "" })} className="text-xs text-[#A0A0A0] hover:text-white">Reset</button>}
                </div>
              </div>
            </div>

          {f.is_creator && empower && (
            <div className="mb-6 rounded-xl border border-[#D4AF37]/30 p-4 space-y-3" data-testid="empower-card">
              <div className="flex items-center justify-between">
                <div className="text-sm text-white font-medium flex items-center gap-2"><Crown size={16} className="text-[#D4AF37]" /> Royalty affirmations</div>
                <button type="button" data-testid="empower-toggle" onClick={() => saveEmpower({ enabled: !empower.enabled })}
                  className={`w-12 h-7 rounded-full transition-colors relative ${empower.enabled ? "gold-gradient" : "bg-[#2A2A2A]"}`}>
                  <span className={`absolute top-1 w-5 h-5 rounded-full bg-black transition-all ${empower.enabled ? "left-6" : "left-1"}`} />
                </button>
              </div>
              <div className="text-xs text-[#A0A0A0]">Empowering royal reminders to keep you feeling like the star you are — by email, in-app, or both. Turn off or change how often anytime.</div>
              {empower.enabled && (
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-[#A0A0A0] mb-1.5">How often</div>
                    <div className="flex flex-wrap gap-2">
                      {[["daily", "Daily"], ["weekly", "Weekly"], ["off", "Pause"]].map(([v, l]) => (
                        <button key={v} type="button" data-testid={`empower-freq-${v}`} onClick={() => saveEmpower({ frequency: v })}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${empower.frequency === v ? "gold-gradient text-black border-transparent font-medium" : "border-[#2A2A2A] text-[#A0A0A0] hover:text-white"}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-[#A0A0A0] mb-1.5">Where</div>
                    <div className="flex flex-wrap gap-2">
                      {[["both", "Email + In-app"], ["email", "Email only"], ["app", "In-app only"]].map(([v, l]) => (
                        <button key={v} type="button" data-testid={`empower-channel-${v}`} onClick={() => saveEmpower({ channel: v })}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${empower.channel === v ? "gold-gradient text-black border-transparent font-medium" : "border-[#2A2A2A] text-[#A0A0A0] hover:text-white"}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-[#A0A0A0] mb-1.5">Narrator voice</div>
                    <div className="flex flex-wrap gap-2">
                      {[["sultry", "Sultry"], ["warm", "Warm"], ["regal", "Regal"], ["playful", "Playful"], ["bright", "Bright"]].map(([v, l]) => (
                        <button key={v} type="button" data-testid={`empower-voice-${v}`} onClick={() => saveEmpower({ voice: v })}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${(empower.voice || "sultry") === v ? "gold-gradient text-black border-transparent font-medium" : "border-[#2A2A2A] text-[#A0A0A0] hover:text-white"}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <button type="button" data-testid="empower-test" onClick={testEmpower} className="btn-outline-gold rounded-full px-5 py-2 text-sm">Send me one now ✨</button>
                </div>
              )}
            </div>
          )}

          {/* Live preview */}
          <div className="text-sm text-[#A0A0A0] mb-3">Preview</div>
          <motion.div key={f.theme} initial={{ opacity: 0.6 }} animate={{ opacity: 1 }}
            className="rounded-2xl p-6" style={{ background: theme.surface, border: `1px solid ${theme.border}` }}>
            <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden border-2" style={{ borderColor: theme.accent }}>
              <img src={f.photos[0] || "https://images.unsplash.com/photo-1769650795569-e51a6e9580c3"} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="font-heading text-3xl text-center" style={{ color: theme.text }}>{f.name || "Your Name"}{f.age ? `, ${f.age}` : ""}</div>
            <div className="text-center text-sm mt-1" style={{ color: theme.accent }}>{f.location || "Somewhere"} · {f.gender || "—"}</div>
            <p className="text-center text-sm mt-3" style={{ color: theme.text, opacity: 0.8 }}>{f.bio || "Your bio will appear here."}</p>
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {f.interests.map((t, i) => (
                <span key={i} className="text-xs px-3 py-1 rounded-full" style={{ border: `1px solid ${theme.accent}`, color: theme.accent }}>{t}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
